import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

export interface LinkedChecklistItem {
  id: string;
  title: string;
  is_completed: boolean;
  management_checked: boolean;
  subcontractor_checked: boolean;
  category: string;
  checklistTitle: string | null;
  confidence: string | null;
  mappingType: string | null;
  mappingId: string | null;
  requires_management_check?: boolean;
  requires_subcontractor_check?: boolean;
}

interface Props {
  scheduleItemId: string;
  sourceTemplateItemId: string | null;
  projectId: string;
  canManage?: boolean;
  onChecklistChanged?: () => void;
}

export default function ScheduleItemModalChecklistSection({
  scheduleItemId,
  sourceTemplateItemId,
  projectId,
  canManage = true,
  onChecklistChanged,
}: Props) {
  const { t } = useTranslation();
  const [items, setItems] = useState<LinkedChecklistItem[]>([]);
  const [allChecklistItems, setAllChecklistItems] = useState<LinkedChecklistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showLinkPanel, setShowLinkPanel] = useState(false);
  const [selectedChecklistId, setSelectedChecklistId] = useState('');
  const [processing, setProcessing] = useState(false);
  const { user } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all checklist items for this project
      const [{ data: ci }, { data: checklists }, { data: templates }] = await Promise.all([
        supabase
          .from('jphph_checklist_items')
          .select('id, title, is_completed, management_checked, subcontractor_checked, category, checklist_id, requires_management_check, requires_subcontractor_check')
          .eq('project_id', projectId)
          .order('sort_order', { ascending: true }),
        supabase
          .from('jphph_checklists')
          .select('id, title')
          .eq('project_id', projectId),
        supabase
          .from('jphph_checklist_template_items')
          .select('id')
          .limit(1),
      ]);

      const clMap = new Map((checklists || []).map((c: { id: string; title: string }) => [c.id, c.title]));
      const allItems = (ci || []).map((c: {
        id: string; title: string; is_completed: boolean;
        management_checked: boolean; subcontractor_checked: boolean;
        category: string; checklist_id: string;
        requires_management_check: boolean; requires_subcontractor_check: boolean;
      }) => ({
        id: c.id,
        title: c.title,
        is_completed: c.is_completed || (c.management_checked && c.subcontractor_checked),
        management_checked: c.management_checked,
        subcontractor_checked: c.subcontractor_checked,
        category: c.category,
        checklistTitle: clMap.get(c.checklist_id) || null,
        confidence: null,
        mappingType: null,
        mappingId: null,
        requires_management_check: c.requires_management_check,
        requires_subcontractor_check: c.requires_subcontractor_check,
      }));
      setAllChecklistItems(allItems);

      // Fetch mappings
      const { data: pm } = await supabase
        .from('jphph_checklist_schedule_item_mappings')
        .select('*')
        .eq('project_schedule_item_id', scheduleItemId);

      const mappingRows: Array<{
        checklist_item_id: string;
        confidence: string | null;
        mapping_type: string | null;
        reason: string | null;
        mapping_id: string;
      }> = [];
      for (const row of (pm || [])) {
        if (row.project_checklist_item_id) {
          mappingRows.push({
            checklist_item_id: row.project_checklist_item_id,
            confidence: row.confidence,
            mapping_type: row.mapping_type,
            reason: row.reason,
            mapping_id: row.id,
          });
        }
      }

      if (sourceTemplateItemId) {
        const { data: tm } = await supabase
          .from('jphph_checklist_schedule_item_mappings')
          .select('*')
          .eq('schedule_template_item_id', sourceTemplateItemId);
        for (const row of (tm || [])) {
          if (row.project_checklist_item_id && !mappingRows.some((m) => m.mapping_id === row.id)) {
            mappingRows.push({
              checklist_item_id: row.project_checklist_item_id,
              confidence: row.confidence,
              mapping_type: row.mapping_type,
              reason: row.reason,
              mapping_id: row.id,
            });
          }
        }
      }

      const ciMap = new Map(allItems.map((c) => [c.id, c]));
      const result: LinkedChecklistItem[] = [];
      for (const mapping of mappingRows) {
        const c = ciMap.get(mapping.checklist_item_id);
        if (!c) continue;
        result.push({
          id: c.id,
          title: c.title,
          is_completed: c.is_completed,
          management_checked: c.management_checked,
          subcontractor_checked: c.subcontractor_checked,
          category: c.category,
          checklistTitle: c.checklistTitle,
          confidence: mapping.confidence,
          mappingType: mapping.mapping_type,
          mappingId: mapping.mapping_id,
          requires_management_check: c.requires_management_check,
          requires_subcontractor_check: c.requires_subcontractor_check,
        });
      }
      setItems(result);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [scheduleItemId, sourceTemplateItemId, projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const unlinkedItems = useMemo(() => {
    const linkedIds = new Set(items.map((i) => i.id));
    return allChecklistItems.filter((c) => !linkedIds.has(c.id));
  }, [allChecklistItems, items]);

  const handleToggleCheck = async (itemId: string, field: 'management_checked' | 'subcontractor_checked') => {
    if (!user) return;
    const item = items.find((i) => i.id === itemId);
    if (!item) return;
    const newVal = !item[field];
    const now = new Date().toISOString();
    const update: Record<string, unknown> = { [field]: newVal };
    if (field === 'management_checked') {
      update.management_checked_at = newVal ? now : null;
      update.management_checked_by = newVal ? user.id : null;
    } else {
      update.subcontractor_checked_at = newVal ? now : null;
      update.subcontractor_checked_by = newVal ? user.id : null;
    }

    // Recalculate is_completed based on requirements
    const requiresMgmt = item.requires_management_check !== false;
    const requiresSub = item.requires_subcontractor_check === true;
    const nextMgmt = field === 'management_checked' ? newVal : item.management_checked;
    const nextSub = field === 'subcontractor_checked' ? newVal : item.subcontractor_checked;
    const nextCompleted = (!requiresMgmt || nextMgmt) && (!requiresSub || nextSub);
    update.is_completed = nextCompleted;
    update.completed_at = nextCompleted ? now : null;

    setItems((prev) =>
      prev.map((i) =>
        i.id === itemId
          ? { ...i, [field]: newVal, is_completed: nextCompleted }
          : i
      )
    );

    await supabase.from('jphph_checklist_items').update(update).eq('id', itemId);

    // Notify parent that checklist changed (triggers progress recalculation)
    onChecklistChanged?.();
  };

  const handleLink = async () => {
    if (!selectedChecklistId) return;
    setProcessing(true);

    // Get a template item ID to satisfy the NOT NULL constraint
    const { data: tmplItems } = await supabase
      .from('jphph_checklist_template_items')
      .select('id')
      .limit(1);

    const templateItemId = tmplItems && tmplItems.length > 0 ? tmplItems[0].id : null;

    const { data, error: err } = await supabase
      .from('jphph_checklist_schedule_item_mappings')
      .insert({
        checklist_template_item_id: templateItemId,
        project_checklist_item_id: selectedChecklistId,
        project_schedule_item_id: scheduleItemId,
        mapping_type: 'manual',
        confidence: 'manual',
        reason: 'Manually linked from schedule item modal',
      })
      .select()
      .maybeSingle();

    if (!err && data) {
      const linked = allChecklistItems.find((c) => c.id === selectedChecklistId);
      if (linked) {
        setItems((prev) => [...prev, { ...linked, mappingId: data.id, mappingType: 'manual', confidence: 'manual' }]);
      }
      setSelectedChecklistId('');
      setShowLinkPanel(false);
      onChecklistChanged?.();
    }
    setProcessing(false);
  };

  const handleUnlink = async (mappingId: string) => {
    setProcessing(true);
    const { error: err } = await supabase
      .from('jphph_checklist_schedule_item_mappings')
      .delete()
      .eq('id', mappingId);

    if (!err) {
      setItems((prev) => prev.filter((i) => i.mappingId !== mappingId));
      onChecklistChanged?.();
    }
    setProcessing(false);
  };

  if (loading) {
    return (
      <div className="py-4 flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-[#E8621A] text-xl"></i>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {items.length === 0 ? (
        <div className="py-3 text-center">
          <p className="text-[#9CA3AF] text-xs">{t('checklist.noChecklistItems')}</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {items.map((item) => {
            const isExpanded = expandedId === item.id;
            return (
              <div
                key={item.id}
                className={`rounded-xl border overflow-hidden transition-colors ${
                  item.is_completed ? 'border-[#059669]/20 bg-[#F0FDF4]/40' : 'border-gray-100 bg-white'
                }`}
              >
                <button
                  onClick={() => setExpandedId(isExpanded ? null : item.id)}
                  className="w-full text-left flex items-center gap-3 px-3 py-2.5 cursor-pointer"
                >
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 border-2"
                    style={{
                      backgroundColor: item.is_completed ? '#F0FDF4' : '#F9FAFB',
                      borderColor: item.is_completed ? '#BBF7D0' : '#E5E7EB',
                    }}
                  >
                    {item.is_completed ? (
                      <i className="ri-check-line text-[#059669] text-xs"></i>
                    ) : (
                      <span className="text-[10px] font-bold text-[#9CA3AF]">
                        {item.management_checked ? 'M' : item.subcontractor_checked ? 'S' : ''}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className={`text-sm font-medium leading-snug truncate ${item.is_completed ? 'text-[#9CA3AF] line-through' : 'text-[#374151]'}`}>
                        {item.title}
                      </p>
                      {item.confidence && item.confidence !== 'high' && (
                        <span className="text-[9px] font-bold text-[#F59E0B] bg-[#FFFBEB] px-1.5 py-0.5 rounded-full flex-shrink-0">
                          {item.confidence}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      {item.checklistTitle && <span className="text-[10px] text-[#9CA3AF]">{item.checklistTitle}</span>}
                      <span className="text-[10px] text-[#9CA3AF] capitalize">{item.category}</span>
                      {item.mappingType && <span className="text-[10px] text-[#D1D5DB]">{item.mappingType}</span>}
                      {item.requires_management_check !== false && (
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${item.management_checked ? 'bg-[#F0FDF4] text-[#059669]' : 'bg-[#FEF0E7] text-[#E8621A]'}`}>
                          {t('checklistCard.mgmt')}: {item.management_checked ? t('checklistCard.complete') : t('checklistCard.mgmtPending')}
                        </span>
                      )}
                      {item.requires_subcontractor_check === true && (
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${item.subcontractor_checked ? 'bg-[#ECFEFF] text-[#0891B2]' : 'bg-[#FFFBEB] text-[#F59E0B]'}`}>
                          {t('checklistCard.sub')}: {item.subcontractor_checked ? t('checklistCard.complete') : t('checklistCard.subPending')}
                        </span>
                      )}
                      {item.is_completed && (
                        <span className="text-[9px] font-bold bg-[#F0FDF4] text-[#059669] px-1.5 py-0.5 rounded-full">{t('checklist.finalCompleteLabel')}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {canManage && item.mappingId && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleUnlink(item.mappingId!); }}
                        disabled={processing}
                        className="w-6 h-6 flex items-center justify-center rounded hover:bg-[#FEF2F2] text-[#D1D5DB] hover:text-[#EF4444] cursor-pointer transition-colors"
                        title={t('btn.delete')}
                      >
                        <i className="ri-close-circle-line text-xs"></i>
                      </button>
                    )}
                    <i className={`${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-[#D1D5DB] text-lg`}></i>
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-3 pb-3 pt-1 border-t border-gray-50">
                    <div className="flex gap-2">
                      <CheckToggleButton
                        checked={item.management_checked}
                        label={t('checklistSection.completeMgmtCheck')}
                        doneLabel={t('checklistCard.mgmt') + ': ' + t('checklistCard.complete')}
                        onClick={() => handleToggleCheck(item.id, 'management_checked')}
                        color="#E8621A"
                      />
                      <CheckToggleButton
                        checked={item.subcontractor_checked}
                        label={t('checklistSection.completeSubCheck')}
                        doneLabel={t('checklistCard.sub') + ': ' + t('checklistCard.complete')}
                        onClick={() => handleToggleCheck(item.id, 'subcontractor_checked')}
                        color="#0891B2"
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Link new item */}
      {canManage && (
        <div className="pt-2">
          {!showLinkPanel ? (
            <button
              onClick={() => setShowLinkPanel(true)}
              className="w-full py-2.5 rounded-xl border-2 border-dashed border-gray-200 text-[#9CA3AF] text-xs font-semibold cursor-pointer whitespace-nowrap hover:border-[#E8621A] hover:text-[#E8621A] transition-colors flex items-center justify-center gap-1.5"
            >
              <i className="ri-add-circle-line"></i>{t('checklist.mapBtn')}
            </button>
          ) : (
            <div className="bg-[#F9FAFB] rounded-xl p-3 space-y-2 border border-gray-100">
              <label className="text-xs font-semibold text-[#555555]">{t('checklist.selectSchedule')}</label>
              <select
                value={selectedChecklistId}
                onChange={(e) => setSelectedChecklistId(e.target.value)}
                className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2 text-sm text-[#374151] cursor-pointer"
              >
                <option value="">{t('checklist.selectScheduleOption')}</option>
                {unlinkedItems.map((c) => (
                  <option key={c.id} value={c.id}>{c.title}</option>
                ))}
              </select>
              <div className="flex gap-2">
                <button
                  onClick={() => { setShowLinkPanel(false); setSelectedChecklistId(''); }}
                  className="flex-1 py-2 rounded-xl bg-white border border-gray-200 text-[#374151] text-xs font-bold cursor-pointer whitespace-nowrap"
                >
                  {t('btn.cancel')}
                </button>
                <button
                  onClick={handleLink}
                  disabled={!selectedChecklistId || processing}
                  className="flex-1 py-2 rounded-xl bg-[#E8621A] text-white text-xs font-bold cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-1"
                >
                  {processing ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-add-circle-line"></i>}
                  {t('checklist.mapBtn')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CheckToggleButton({
  checked,
  label,
  doneLabel,
  onClick,
  color,
}: {
  checked: boolean;
  label: string;
  doneLabel: string;
  onClick: () => void;
  color: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold cursor-pointer whitespace-nowrap border transition-colors ${
        checked ? 'border-transparent text-white' : 'border-gray-100 text-[#374151] bg-white hover:border-gray-200'
      }`}
      style={checked ? { backgroundColor: color } : {}}
    >
      <i className={`${checked ? 'ri-checkbox-circle-fill' : 'ri-checkbox-blank-circle-line'} text-xs`}></i>
      {checked ? doneLabel : label}
    </button>
  );
}